import type { Timestamp } from 'firebase/firestore'

// ─── Enums ───────────────────────────────────────────────────────────────────

export enum IncidentStatus {
  Active = 'active',
  Suspended = 'suspended',
  Closed = 'closed',
  Draft = 'draft',
}

export type MissionType = 'lost_person' | 'rescue' | 'recovery' | 'other'

export enum TeamType {
  Ground = 'GROUND',
  K9 = 'K9',
  Air = 'AIR',
  Bike = 'BIKE',
  Type1 = 'TYPE1',
  Rescue = 'RESCUE',
  Overhead = 'OVERHEAD',
}

export enum TeamStatus {
  Building = 'Building',
  AwaitingAssignment = 'Awaiting Assignment',
  Assigned = 'Assigned',
  Enroute = 'Enroute',
  OnScene = 'In Assignment',
  RTB = 'Returning',
  Returned = 'Returned',
  Emergency = 'Emergency',
}

export enum AssignmentStatus {
  Draft = 'Draft',
  NotStarted = 'Not Started',
  InProgress = 'In Progress',
  Completed = 'Completed',
}

export enum PersonnelStatus {
  Available = 'available',
  Assigned = 'assigned',
  OutOfService = 'out-of-service',
}

export enum SearcherType {
  Type1 = 'Type 1',
  Type2 = 'Type 2',
  Type3 = 'Type 3',
}

export enum RopeQual {
  None = 'None',
  Awareness = 'Rope Awareness',
  Operator = 'Rope Operator',
  Technician = 'Rope Technician',
}

export enum MedicalQual {
  None = 'None',
  EMR = 'EMR/PSFA/WFR',
  EMT = 'EMT',
  NurseParamedic = 'Nurse/Paramedic',
  MD = 'MD',
}

// Standard misc qualifications. Agencies can extend this list via Agency.custom_quals.
export enum MiscQual {
  UAV = 'UAV',
  Overhead = 'Overhead',
  EBike = 'EBike',
  SwiftWater = 'Swift Water',
  K9 = 'K9 Handler',
  Flanker = 'K9 Flanker',
  TrackingTrailing = 'Tracking/Trailing',
  Avalanche = 'Avalanche (Avy 1)',
  ATR = 'Large Animal Rescue (ATR)',
  Driver = 'Driver',
  UTV = 'UTV',
  Dive = 'Dive',
  FLO = 'Family Liaison Officer',
}

export enum OverheadRole {
  IC = 'Incident Commander',
  OpsChief = 'Operations Section Chief',
  Operations = 'Operations',
  PlansChief = 'Plans Section Chief',
  Plans = 'Plans',
  Logistics = 'Logistics',
  SearchManager = 'Search Manager',
  Communications = 'Communications',
  FamilyLiaisonOfficer = 'Family Liaison Officer',
  Investigations = 'Investigations',
  GeneralOverhead = 'General Overhead',
}

export const OVERHEAD_ROLE_OPTIONS: string[] = Object.values(OverheadRole)

export const SEARCHER_TYPE_OPTIONS: string[] = Object.values(SearcherType)

export const ROPE_QUAL_OPTIONS: string[] = Object.values(RopeQual)

export const STANDARD_MISC_QUALS: string[] = Object.values(MiscQual)

export const MEDICAL_QUAL_OPTIONS: string[] = Object.values(MedicalQual)

export enum MessageType {
  PriorityTraffic = 'Priority Traffic',
  EmergencyTraffic = 'Emergency Traffic',
  RadioCheck = 'Radio Check',
  Enroute = 'Enroute',
  Returning = 'Returning to ICP',
  StartingAssignment = 'Starting Assignment',
  WelfareCheck = 'Welfare Check',
  CompletedAssignment = 'Completed Assignment',
  RequestingAssignment = 'Requesting Assignment',
  ArrivedAtICP = 'Arrived at ICP',
  Clue = 'Clue',
  Other = 'Other',
}

export const MESSAGE_TYPES: string[] = Object.values(MessageType)

export enum PriorityTrafficPlan {
  A = 'A',
  B = 'B',
  C = 'C',
}

export const PRIORITY_TRAFFIC_PLAN_LABELS: Record<string, string> = {
  [PriorityTrafficPlan.A]: 'A — Alive and well',
  [PriorityTrafficPlan.B]: 'B — Needs Assistance',
  [PriorityTrafficPlan.C]: 'C — Deceased',
}

export const PRIORITY_TRAFFIC_RESOURCES: string[] = [
  'Litter',
  'Litter Wheel',
  'Helicopter',
  'O2',
  'Med Bag',
  'Trail Rescue Kit',
  'Low Angle Kit',
  'High Angle Kit',
  'UTV',
  'Other',
]

export interface PriorityTrafficIncident {
  pti_id: string
  status: 'active' | 'resolved'
  declared_at: Timestamp
  declared_by_team_id: string
  declared_by_team_identifier: string
  triggering_radio_log_entry_id: string | null
  location_address: string | null
  location_coordinates: string | null
  resources_requested: string[]
  notes: string | null
  resolved_at: Timestamp | null
  resolved_by_user_id: string | null
  resolved_by_name: string | null
}

export enum FormType {
  ICS201 = 'ICS201',
  ICS211Header = 'ICS211_header',
  ICS214 = 'ICS214',
  SAR104S = 'SAR104S',
  SAR110 = 'SAR110',
  SAR111 = 'SAR111',
  SAR134 = 'SAR134',
}

/** Agency-scoped roles — stored on the user profile. */
export const AGENCY_ROLES = ['admin', 'member'] as const

// ─── Entities ─────────────────────────────────────────────────────────────────

export interface Agency {
  agency_id: string
  agency_name: string
  agency_logo: string
  // Agency-specific qualifications appended to STANDARD_MISC_QUALS in selection UIs
  custom_quals: string[]
  // Auto-incrementing counter for mission number generation
  last_incident_number?: number
  incident_number_prefix?: string
  // D4H web app base URL, e.g. https://marin.team-manager.us.d4h.com. Set by PUT /api/d4h/config.
  d4h_web_url?: string
}

export interface User {
  user_id: string
  user_first_name: string
  user_last_name: string
  agency_id: string
  email: string
  medical_qual: string
  searcher_type: SearcherType | null
  rope_qual: RopeQual | null
  // Values are MiscQual members plus any agency custom_quals entries
  misc_quals: string[]
  phone: string
  google_id: string | null
  role: typeof AGENCY_ROLES[number]
  is_active?: boolean
  is_super_admin?: boolean
  // Set for agency-less guests invited to a single incident. When true,
  // agency_id is absent and access is limited to guest_incident_id.
  guest?: boolean
  guest_incident_id?: string | null
}

export interface AgencyInvite {
  invite_id: string
  agency_id: string
  email: string
  invited_by_uid: string
  created_at: Timestamp
  expires_at: Timestamp
  claimed_by_uid: string | null
  claimed_at: Timestamp | null
}

// Agency roster member imported from D4H. Written only by the backend sync.
// Stored at agencies/{agencyId}/roster/{d4h_member_id}.
export interface RosterMember {
  roster_id: string
  source: 'd4h'
  external_id: string
  first: string
  last: string
  // From "(Bear)" in the D4H name. Absent on docs synced before it was added.
  nickname?: string
  email: string
  phone: string
  medical_qual: MedicalQual | null
  searcher_type: SearcherType | null
  rope_qual: RopeQual | null
  misc_quals: string[]
  d4h_qualifications: string[]
  operational: boolean
  // Set when the member's email matches a FoS user in the same agency
  user_id: string | null
  active: boolean
  synced_at: Timestamp
}

export const D4H_REGIONS = ['us', 'eu', 'ca', 'ap'] as const
export type D4HRegion = typeof D4H_REGIONS[number]

// FoS qualification fields a D4H qualification can be mapped onto
export const D4H_QUAL_FIELDS = ['medical_qual', 'searcher_type', 'rope_qual', 'misc_quals'] as const
export type D4HQualField = typeof D4H_QUAL_FIELDS[number]

export interface D4HQualMappingEntry {
  field: D4HQualField
  value: string
}

// The signed-in person's own roster record, returned by GET /api/d4h/roster-match during onboarding
export interface D4HRosterMatch {
  first: string
  last: string
  phone: string
  medical_qual: MedicalQual | null
  searcher_type: SearcherType | null
  rope_qual: RopeQual | null
  misc_quals: string[]
  team_name: string
}

// A D4H member qualification and how many current members hold it
export interface D4HQualification {
  title: string
  holders: number
}

export interface D4HSyncSummary {
  added: number
  updated: number
  deactivated: number
  linked: number
  total: number
}

// Public view of agencies/{id}/d4h_config/default (the API never returns the token)
export interface D4HConfig {
  configured: boolean
  region: D4HRegion
  team_id: number | null
  team_name: string
  // "marin" in marin.team-manager.us.d4h.com
  subdomain: string | null
  qual_mapping: Record<string, D4HQualMappingEntry>
  last_sync_at: string | null
  last_sync_summary: D4HSyncSummary | null
}

export interface AgencyDomainRule {
  domain: string
  agency_id: string
  created_by_uid: string
  created_at: Timestamp
}

export interface AgencyJoinRequest {
  request_id: string
  user_id: string
  agency_id: string
  agency_name: string
  user_display_name: string
  user_email: string
  requested_at: Timestamp
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn'
  reviewed_by_uid: string | null
  reviewed_at: Timestamp | null
}

// ─── Permissions ──────────────────────────────────────────────────────────────

export type ActionKey =
  | 'incident.create_close'
  | 'incident.edit'
  | 'op.create'
  | 'team.create_manage'
  | 'team.update_status'
  | 'assignment.create'
  | 'assignment.debrief'
  | 'personnel.checkin'
  | 'personnel.checkout_others'
  | 'forms.edit'
  | 'forms.view'
  | 'clues.log'
  | 'forms.export_pdf'
  | 'audit.view'
  | 'stats.view'
  | 'agency.logo_upload'
  | 'personnel.generate_qr'
  | 'personnel.invite'
  | 'subjects.generate_flyer'
  | 'radio_log.log_entry'
  | 'radio_log.edit_entry'
  | 'radio_log.delete_entry'
  | 'radio_log.export_ics309'
  | 'iwi.declare'
  | 'iwi.resolve'
  | 'iwi.checklist'
  | 'priority_traffic.create'
  | 'priority_traffic.resolve'

export type AppRole = string

export type PermissionConfig = Record<ActionKey, AppRole[]>

/** Incident-scoped (operational) roles — used in user_ops records. */
export const OP_ROLES = [
  ...Object.values(OverheadRole),
  'Team Leader',
  'Team Member',
] as const

export const SUBJECT_CATEGORIES = ['Hiker', 'Despondant', 'Dementia', 'Climber', 'Cyclist', 'Hunter', 'Other'] as const
export const MOBILITY_OPTIONS = ['Unknown', 'Ambulatory', 'Limited', 'Non-ambulatory'] as const
export const TERRAIN_OPTIONS = ['Wilderness', 'Urban', 'Water', 'Technical', 'Cave/Mine', 'Mixed'] as const

export interface Incident {
  incident_id: string
  incident_name: string
  incident_number: string | null
  agency_id: string
  status: IncidentStatus
  mission_type: MissionType | null
  outcome: 'found' | 'not_found' | 'suspended' | null
  created_by: string
  created_at: Timestamp
  updated_at: Timestamp
  closed_at: Timestamp | null
  contact_phone: string | null
  secondary_agency_logo: string | null
  requesting_agency_id: string | null
  requesting_agency_name: string | null
  start_date: string | null
  start_time: string | null
  icp_location: string | null
  situation_summary: string | null
}

export interface ObjectiveTemplate {
  id: string
  mission_types: string[]
  text: string
  order: number
}

export interface WizardChannel {
  id: string
  name: string
  assignment: string     // "Command" | "Tactical" | "Air-to-Ground"
  rx_freq: string
  rx_tone: string
  tx_freq: string
  tx_tone: string
  mode: string           // "A" | "D" | "M"
  remarks: string
  include: boolean
  is_custom: boolean
  preset_id?: string
}

export interface AgencyChannelPreset {
  preset_id: string
  name: string
  rx_freq: string
  rx_tone: string
  tx_freq: string
  tx_tone: string
  mode: string
  remarks: string
  order: number
}

export interface CommsChannel {
  channel_id: string
  name: string
  assignment: string
  rx_freq: string
  rx_tone: string
  tx_freq: string
  tx_tone: string
  mode: string
  remarks: string
  order: number
  created_at: Timestamp
  zone_group: string
  channel_number: string
}

export interface Subject {
  subject_id: string
  incident_id: string
  first_name: string
  last_name: string
  age: number | null
  subject_category: string
  mobility: string
  terrain_types: string[]
  gender: string
  physical_description: string
  medical_conditions: string
  medications: string
  experience_level: string
  behavior_if_lost: string
  survival_training: string
  lkp_lat: number | null
  lkp_lng: number | null
  lkp_description: string
  lkp_time: Timestamp | null
  lkp_witness: string
  intended_dest: string
  planned_route: string
  expected_return: string
  overdue_by: string
  vehicle_description: string
  vehicle_location: string
  companions: string
  companion_names: string
  date_reported: string | null
  time_reported: string | null
  height: string
  weight: string
  race: string
  complexion: string
  hair_color: string
  eye_color: string
  clothing_description: string
  photo_url: string | null
  risk_category: string | null
  reporter_name: string
  reporter_relationship: string
  reporter_phone: string
  how_reported: string
  created_at: Timestamp
  flyer_draft?: FlyerData | null
  permanent_flyer_url?: string | null
}

export interface FlyerData {
  risk_category_label: string
  subject_name: string
  missing_since: string
  last_seen: string
  age: string
  sex: string
  height: string
  weight: string
  race: string
  complexion: string
  hair: string
  eyes: string
  clothing: string
  situation: string
  contact_phone: string
}

export interface FlyerVersion {
  version_id: string
  version_number: number
  created_at: Timestamp
  created_by_uid: string
  created_by_name: string
  flyer_data: FlyerData
  pdf_url: string
}

export interface OperationalPeriod {
  op_id: string
  incident_id: string
  op_number: number
  date: string
  created_by: string
  icp_location: string | null
  contact_phone: string | null
  caltopo_map_id: string | null
  caltopo_map_name: string | null
  caltopo_op_id: string | null
  caltopo_poll_interval_ms: number | null
  op_start_date: string | null
  op_start_time: string | null
  op_end_date: string | null
  op_end_time: string | null
  safety_message: string | null
  weather_forecast: string | null
  site_safety_plan_required: boolean
  site_safety_plan_location: string | null
}

export interface Team {
  team_id: string
  op_id: string
  team_type: TeamType
  team_identifier: string
  team_status: TeamStatus
  team_leader_personnel_id: string | null
}

export interface Assignment {
  assignment_id: string
  op_id: string
  assignment_name: string
  description: string
  assignment_status: AssignmentStatus
  prev_search_efforts: string
  time_allocated: string
  size_of_assignment: string
  // CalTopo-sourced fields (read-only in FoS)
  caltopo_assignment_id: string | null
  letter: string | null
  number: string | null
  previous_efforts: string | null
  prepared_by: string | null
  caltopo_op_id: string | null
  geometry: string | null  // GeoJSON serialized to string (Firestore can't store nested arrays)
  last_synced_at: Timestamp | null
  caltopo_deleted: boolean
  // Bidirectional fields (FoS edits write back to CalTopo)
  resource_type: 'GROUND' | 'AIR' | 'WATER' | 'K9' | null
  team_size: string | null
  priority: 'HIGH' | 'MEDIUM' | 'LOW' | null
  responsive_pod: 'HIGH' | 'MEDIUM' | 'LOW' | null
  unresponsive_pod: 'HIGH' | 'MEDIUM' | 'LOW' | null
  clue_pod: 'HIGH' | 'MEDIUM' | 'LOW' | null
  transportation: string | null
  primary_frequency: string | null
  secondary_frequency: string | null
  // FoS-only manual priority ordering (drag-to-reorder); distinct from CalTopo priority field
  sort_order: number | null
}

export interface TeamAssignment {
  ta_id: string
  assignment_id: string
  team_id: string
  assigned_at: Timestamp
  started_at: Timestamp | null
  completed_at: Timestamp | null
  debrief_cols: Record<string, unknown>
}

export interface UserOp {
  user_op_id: string
  user_id: string | null
  personnel_id: string
  op_id: string
  team_id: string | null
  active: boolean
  role: string
}

export interface Personnel {
  personnel_id: string
  incident_id: string
  op_id: string
  user_id: string | null
  last: string
  first: string
  agency: string
  phone: string
  when_made: string
  time_in: Timestamp
  time_out: Timestamp | null
  resource: string
  qualifications: Record<string, unknown>
  category: string
  status: PersonnelStatus
  team_id: string | null
  notes: string
}

export interface Clue {
  clue_id: string
  incident_id: string
  description: string
  lat: number | null
  lng: number | null
  status: 'unconfirmed' | 'confirmed' | 'resolved'
  found_by: string
  found_at: Timestamp
  caltopo_clue_id: string | null
  title: string | null
  location: string | null
  instructions: string | null
  caltopo_assignment_id: string | null
  last_synced_at: Timestamp | null
  caltopo_deleted: boolean
}

export interface MapLocation {
  location_id: string
  caltopo_marker_id: string
  title: string
  symbol: string
  lat: number
  lng: number
  caltopo_deleted: boolean
}

export interface CalTopoMap {
  id: string
  title: string
  updated: number
}

export interface FormData {
  form_id: string
  incident_id: string
  form_type: FormType
  data: Record<string, unknown>
  last_updated_by: string
  last_updated_at: Timestamp
}

export interface RadioLogEntry {
  entry_id: string
  client_timestamp: Timestamp
  team_identifier: string
  team_id: string | null
  message_type: string
  assignment_id: string | null
  assignment_name: string | null
  priority_traffic_plan: string | null
  notes: string | null
  operator_id: string
  operator_name: string
  deleted_at: Timestamp | null
  edited_at: Timestamp | null
  edited_by_name: string | null
}

export type IwiTransport = 'air' | 'ground' | 'self'

export interface Iwi {
  iwi_id: string
  status: 'active' | 'resolved'
  declared_at: Timestamp
  declared_by_team_id: string
  declared_by_team_identifier: string
  triggering_radio_log_entry_id: string | null

  chief_complaint: string | null
  severity_life_threatening: boolean | null
  patient_location: string | null
  point_of_contact: string | null
  transport: IwiTransport | null
  pickup_location: string | null
  patient_name: string | null
  patient_age: string | null
  patient_sex: string | null
  patient_agency: string | null
  team_number: string | null
  emt_with_patient: boolean | null
  emt_name: string | null

  checklist_team_leader_contacted: boolean
  checklist_ops_notified: boolean
  checklist_support_requested: boolean
  checklist_team_leader_is_poc: boolean
  checklist_air_ground_channel: boolean
  checklist_command_channel_cleared: boolean

  resolved_at: Timestamp | null
  resolved_by_user_id: string | null
  resolved_by_name: string | null
}

export interface AuditLog {
  log_id: string
  incident_id: string
  user_id: string
  user_name: string
  action: string
  entity_type: string
  entity_id: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  timestamp: Timestamp
}

// ─── IAP Builder Types ────────────────────────────────────────────────────────

export interface IAPData {
  status: 'not_started' | 'draft'
  prepared_by_202: string
  prepared_by_203: string
  prepared_by_205: string
  prepared_by_206: string
  prepared_by_208: string
  created_at: Timestamp
}

export interface OperationalObjective {
  objective_id: string
  text: string
  order: number
  created_at: Timestamp
}

export interface OrgAssignment {
  incident_commander: { name: string; agency: string }
  search_manager: { name: string; agency: string }
  plans_chief: string
  plans_deputy: string
  ops_chief: string
  ops_deputy: string
  logistics_chief: string
  logistics_deputy: string
  intel_chief: string
  pio: string
  liaison: string
  safety_officer: string
  agency_reps: { name: string; agency: string }[]
  divisions: { label: string; supervisor: string }[]
  comms: string
  medical: string
  food_water: string
  transportation: string[]
}

export interface ICS206AidStation {
  name: string
  location: string
  phone: string
  paramedics_on_site: boolean
  level: 'ALS' | 'BLS'
}

export interface ICS206Transport {
  service: string
  location: string
  phone: string
  level: 'ALS' | 'BLS'
}

export interface ICS206Hospital {
  name: string
  address: string
  phone: string
  travel_time_air: string
  travel_time_ground: string
  trauma_center: boolean
  burn_center: boolean
  helipad: boolean
}

export interface MedicalPlanData {
  aid_stations: ICS206AidStation[]
  transportation: ICS206Transport[]
  hospitals: ICS206Hospital[]
  emergency_procedures: string
  k9_vet_info: string
  medical_unit_leader: string
}

export interface IncidentHazard {
  hazard_id: string
  hazard: string
  mitigation: string
  order: number
  created_at: Timestamp
}

export interface AgencyHazardTemplate {
  template_id: string
  hazard: string
  mitigation: string
  order: number
}
